/**
 * Note Mapping Calculations
 * Based on spec §5.3 - Mapowanie muzyczne
 *
 * ## Coordinate System and Musical Directions
 *
 * This module implements isomorphic note mapping on a hexagonal grid.
 * The key insight (from SMC 2011 paper) is that isomorphic layouts are
 * defined by TWO musical direction vectors (H and V), not by axis intervals.
 *
 * MODULE_KEYS uses proper axial hex coordinates where all 6 physical
 * neighbor directions produce constant (Δq, Δr):
 *
 *   Direction    | Δq | Δr | Visual direction
 *   -------------|----|----|------------------
 *   Right        | +1 |  0 | same row, right
 *   Lower-right  | +1 | -1 | down-right diagonal
 *   Lower-left   |  0 | -1 | down-left diagonal
 *   Left         | -1 |  0 | same row, left
 *   Upper-left   | -1 | +1 | up-left diagonal
 *   Upper-right  |  0 | +1 | up-right diagonal
 *
 * Note: r increases going UP visually (r=1 bottom, r=4 top).
 *
 * ## Mapping Formula
 *
 * The formula note = base + q*interval_q + r*interval_r computes the note
 * for any (q, r) position. Since adjacent keys are exactly Δq=1 or Δr=1
 * apart, the intervals directly represent semitone steps between neighbors:
 *
 *   Right (+1, 0):     +interval_q
 *   Upper-right (0,+1): +interval_r
 *   Upper-left (-1,+1): -interval_q + interval_r
 *
 * This guarantees isomorphic note mapping: the same physical movement
 * always produces the same musical interval, regardless of position.
 */

import type { HexCoord } from '@/types/hex';
import type { NoteMapping } from '@/types/config';

/**
 * Calculate MIDI note number for a hexagonal coordinate
 * Spec §5.3: Note mapping formula (NORMATIVE)
 *
 * Formula: note = base_note + (delta_q × interval_q) + (delta_r × interval_r)
 *
 * With proper axial coordinates, adjacent keys are at unit steps:
 * - Same row neighbor: Δq=±1 → ±interval_q semitones
 * - Upper-right neighbor: Δr=+1 → +interval_r semitones
 * - Upper-left neighbor: Δq=-1,Δr=+1 → -interval_q + interval_r semitones
 *
 * @param coord - Global hexagonal coordinate
 * @param mapping - Note mapping configuration
 * @returns MIDI note number (0-127), clamped to valid range
 */
export function calculateNote(
  coord: HexCoord,
  mapping: NoteMapping
): number {
  const deltaQ = coord.q - mapping.origin.q;
  const deltaR = coord.r - mapping.origin.r;

  const note =
    mapping.base_note +
    deltaQ * mapping.intervals.q +
    deltaR * mapping.intervals.r;

  // Clamp to valid MIDI range (0-127)
  return Math.max(0, Math.min(127, note));
}

/**
 * Calculate notes for all coordinates in a grid
 *
 * @param coords - Array of hexagonal coordinates
 * @param mapping - Note mapping configuration
 * @returns Map of coordinate string keys to MIDI note numbers
 */
export function calculateAllNotes(
  coords: HexCoord[],
  mapping: NoteMapping
): Map<string, number> {
  const noteMap = new Map<string, number>();

  for (const coord of coords) {
    const note = calculateNote(coord, mapping);
    const key = `${coord.q},${coord.r}`;
    noteMap.set(key, note);
  }

  return noteMap;
}

/**
 * Get MIDI note name from note number
 *
 * @param note - MIDI note number (0-127)
 * @returns Note name with octave (e.g., "C4", "F#3", "Bb5")
 */
export function getNoteName(note: number): string {
  const noteNames = [
    'C',
    'C#',
    'D',
    'D#',
    'E',
    'F',
    'F#',
    'G',
    'G#',
    'A',
    'A#',
    'B',
  ];

  const octave = Math.floor(note / 12) - 1;
  const noteName = noteNames[note % 12];

  return `${noteName}${octave}`;
}

/**
 * Get alternative note name (flats instead of sharps)
 *
 * @param note - MIDI note number (0-127)
 * @returns Note name with flats (e.g., "Db4" instead of "C#4")
 */
export function getNoteNameFlat(note: number): string {
  const noteNames = [
    'C',
    'Db',
    'D',
    'Eb',
    'E',
    'F',
    'Gb',
    'G',
    'Ab',
    'A',
    'Bb',
    'B',
  ];

  const octave = Math.floor(note / 12) - 1;
  const noteName = noteNames[note % 12];

  return `${noteName}${octave}`;
}

/**
 * Parse note name to MIDI note number
 *
 * @param noteName - Note name (e.g., "C4", "F#3", "Bb5")
 * @returns MIDI note number or null if invalid
 */
export function parseNoteName(noteName: string): number | null {
  const match = noteName.match(/^([A-G][#b]?)(-?\d+)$/);
  if (!match) return null;

  const note = match[1];
  const octave = parseInt(match[2] ?? '0', 10);

  const noteValues: Record<string, number> = {
    C: 0,
    'C#': 1,
    Db: 1,
    D: 2,
    'D#': 3,
    Eb: 3,
    E: 4,
    F: 5,
    'F#': 6,
    Gb: 6,
    G: 7,
    'G#': 8,
    Ab: 8,
    A: 9,
    'A#': 10,
    Bb: 10,
    B: 11,
  };

  const noteValue = noteValues[note ?? ''];
  if (noteValue === undefined) return null;

  const midiNote = (octave + 1) * 12 + noteValue;

  // Validate range
  if (midiNote < 0 || midiNote > 127) return null;

  return midiNote;
}

/**
 * Check if a note is a scale degree relative to root
 *
 * @param note - MIDI note number
 * @param rootNote - Root note of the scale
 * @param scaleDegree - Scale degree (0 = root, 2 = third, 4 = fifth, etc.)
 * @returns true if note matches the scale degree in any octave
 */
export function isScaleDegree(
  note: number,
  rootNote: number,
  scaleDegree: number
): boolean {
  const noteClass = note % 12;
  const rootClass = rootNote % 12;
  const degreeClass = (rootClass + scaleDegree) % 12;

  return noteClass === degreeClass;
}

/**
 * Direction vectors for the MODULE_KEYS coordinate system
 *
 * With proper axial coordinates, these are the standard 6 hex directions.
 * Each direction is constant regardless of position (isomorphic property).
 */
export const MODULE_KEYS_DIRECTIONS = {
  /** Adjacent horizontal keys (same row, to the right): Δq=+1, Δr=0 */
  horizontalRight: { dq: 1, dr: 0 },

  /** Upper-right diagonal: Δq=0, Δr=+1 */
  upperRight: { dq: 0, dr: 1 },

  /** Upper-left diagonal: Δq=-1, Δr=+1 */
  upperLeft: { dq: -1, dr: 1 },

  /** Lower-right diagonal: Δq=+1, Δr=-1 */
  lowerRight: { dq: 1, dr: -1 },

  /** Lower-left diagonal: Δq=0, Δr=-1 */
  lowerLeft: { dq: 0, dr: -1 },

  /** Adjacent horizontal keys (same row, to the left): Δq=-1, Δr=0 */
  horizontalLeft: { dq: -1, dr: 0 },
};

/**
 * Calculate interval_q and interval_r from desired H and V semitone intervals
 *
 * This helper resolves the H/V direction system (from SMC 2011) to the
 * q/r coordinate intervals used in the note mapping formula.
 *
 * @param hSemitones - Semitones for horizontal direction movement (same row right)
 * @param vSemitones - Semitones for upper-right diagonal movement
 * @param hDirection - Direction vector for H (default: horizontal right (+1,0))
 * @param vDirection - Direction vector for V (default: upper-right (0,+1))
 * @returns Object with interval_q and interval_r values
 *
 * @example
 * // Wicki-Hayden: H=2 (whole tone right), V=7 (fifth up-right)
 * const intervals = calculateIntervalsFromHV(2, 7);
 * // Returns { q: 2, r: 7 }
 */
export function calculateIntervalsFromHV(
  hSemitones: number,
  vSemitones: number,
  hDirection: { dq: number; dr: number } = MODULE_KEYS_DIRECTIONS.horizontalRight,
  vDirection: { dq: number; dr: number } = MODULE_KEYS_DIRECTIONS.upperRight
): { q: number; r: number } {
  // Solve the system of equations:
  // hDirection.dq * interval_q + hDirection.dr * interval_r = hSemitones
  // vDirection.dq * interval_q + vDirection.dr * interval_r = vSemitones

  const det =
    hDirection.dq * vDirection.dr - hDirection.dr * vDirection.dq;

  if (det === 0) {
    throw new Error(
      'H and V directions are parallel - cannot determine unique intervals'
    );
  }

  const interval_q =
    (hSemitones * vDirection.dr - vSemitones * hDirection.dr) / det;
  const interval_r =
    (vSemitones * hDirection.dq - hSemitones * vDirection.dq) / det;

  return { q: interval_q, r: interval_r };
}

/**
 * Preset note mappings for common isomorphic layouts
 *
 * With proper axial coordinates, adjacent keys are Δq=1 apart in the same
 * row and Δr=1 apart between rows. The intervals directly give the semitone
 * step for each physical neighbor direction:
 *
 *   Right (+1, 0):      +interval_q
 *   Upper-right (0,+1): +interval_r
 *   Upper-left (-1,+1): -interval_q + interval_r
 *
 * ## Lumatone-compatible layouts (from Xenharmonic Wiki)
 *
 * | Layout         | Right (row) | Up-right    | Up-left     |
 * |----------------|-------------|-------------|-------------|
 * | Wicki-Hayden   | +2 (M2)    | +7 (P5)     | +5 (P4)     |
 * | Harmonic Table | +4 (M3)    | +7 (P5)     | +3 (m3)     |
 * | Gerhard        | +7 (P5)    | +4 (M3)     | -3 (m3 dn)  |
 * | Chromatic      | +4 (M3)    | +1 (m2)     | -3 (m3 dn)  |
 */
export const presetMappings = {
  /**
   * Wicki-Hayden layout (Lumatone Melodic Mode)
   *
   * - Right: +2 semitones (whole tone)
   * - Upper-right: +7 semitones (perfect fifth)
   * - Upper-left: +5 semitones (perfect fourth)
   *
   * Whole tone scale along rows, fifths diagonally up-right.
   * Matches Lumatone "Preset 2 - Melodic Mode".
   */
  wickiHayden: {
    origin: { q: 0, r: 1 },
    base_note: 48,
    intervals: { q: 2, r: 7 },
  } as NoteMapping,

  /**
   * Harmonic Table layout (Lumatone Harmonic Mode)
   *
   * - Right: +4 semitones (major third)
   * - Upper-right: +7 semitones (perfect fifth)
   * - Upper-left: +3 semitones (minor third)
   *
   * Triads cluster in compact triangles:
   * - Major: root + right neighbor + upper-right neighbor
   * - Minor: root + upper-left neighbor + upper-right neighbor
   * Matches Lumatone "Preset 3 - Harmonic Mode".
   */
  harmonicTable: {
    origin: { q: 0, r: 1 },
    base_note: 60,
    intervals: { q: 4, r: 7 },
  } as NoteMapping,

  /**
   * Gerhard layout
   *
   * - Right: +7 semitones (perfect fifth)
   * - Upper-right: +4 semitones (major third)
   * - Upper-left: -3 semitones (minor third down)
   *
   * Fifths along the row, thirds diagonally. Good for harmonic exploration.
   */
  gerhard: {
    origin: { q: 0, r: 1 },
    base_note: 60,
    intervals: { q: 7, r: 4 },
  } as NoteMapping,

  /**
   * Chromatic layout
   *
   * - Right: +4 semitones (major third)
   * - Upper-right: +1 semitone (minor second)
   * - Upper-left: -3 semitones (minor third down)
   *
   * All 12 chromatic notes within one module (12 keys = 12 unique pitches).
   */
  chromatic: {
    origin: { q: 0, r: 1 },
    base_note: 60,
    intervals: { q: 4, r: 1 },
  } as NoteMapping,

  /**
   * Whole-tone layout
   *
   * - Right: +2 semitones (whole tone)
   * - Upper-right: +2 semitones (whole tone)
   * - Upper-left: 0 semitones (unison)
   *
   * Whole-tone scale in row and diagonal directions.
   */
  wholeTone: {
    origin: { q: 0, r: 1 },
    base_note: 60,
    intervals: { q: 2, r: 2 },
  } as NoteMapping,

  /**
   * Fourths layout (guitar-like tuning)
   *
   * - Right: +5 semitones (perfect fourth)
   * - Upper-right: +5 semitones (perfect fourth)
   * - Upper-left: 0 semitones (unison)
   *
   * Like guitar open tuning, fourths in both directions.
   */
  fourths: {
    origin: { q: 0, r: 1 },
    base_note: 60,
    intervals: { q: 5, r: 5 },
  } as NoteMapping,
};
